import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, DatePicker, Row, Space, Statistic, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment, { Moment } from 'moment';
import { formatNu } from '../utils/formatAmount';
import { getTdsReport, TdsReport } from '../services/vendorSettlementService';
import './Reports.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const TdsFiling: React.FC = () => {
  const [range, setRange] = useState<[Moment, Moment]>([
    moment().startOf('month'),
    moment().endOf('day'),
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<TdsReport | null>(null);

  const startDate = range[0].format('YYYY-MM-DD');
  const endDate = range[1].format('YYYY-MM-DD');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        setReport(await getTdsReport(startDate, endDate));
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Could not load TDS report');
        setReport(null);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [startDate, endDate]);

  const columns: ColumnsType<TdsReport['rows'][number]> = [
    { title: 'Date', dataIndex: 'settlement_date', width: 110 },
    { title: 'Settlement #', dataIndex: 'settlement_number', width: 160 },
    { title: 'Vendor', dataIndex: 'vendor_name' },
    { title: 'Code', dataIndex: 'vendor_code', width: 100 },
    {
      title: 'Gross',
      dataIndex: 'gross_amount',
      align: 'right',
      render: (v) => formatNu(Number(v) || 0),
    },
    {
      title: 'Rate',
      dataIndex: 'tds_rate',
      width: 80,
      render: (v) => (v != null ? `${v}%` : '—'),
    },
    {
      title: 'TDS withheld',
      dataIndex: 'tds_amount',
      align: 'right',
      render: (v) => formatNu(Number(v) || 0),
    },
    {
      title: 'Paid supplier',
      dataIndex: 'net_paid',
      align: 'right',
      render: (v) => formatNu(Number(v) || 0),
    },
    {
      title: 'Certificate #',
      dataIndex: 'withholding_certificate_no',
      render: (v) => v || '—',
    },
  ];

  return (
    <div style={{ padding: '20px 24px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            TDS withheld (supplier settlements)
          </Title>
          <Text type="secondary">
            Only settlements where TDS was withheld. Record payments from Vendors → $ icon. Does not change stock.
          </Text>
        </div>
        <Space>
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) setRange([dates[0], dates[1]]);
            }}
          />
        </Space>
      </div>

      {error ? <Alert type="warning" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Gross settled" prefix="Nu." value={Number(report?.total_gross || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="TDS for DRC" prefix="Nu." value={Number(report?.total_tds || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Paid to suppliers" prefix="Nu." value={Number(report?.total_net_paid || 0)} precision={2} />
          </Card>
        </Col>
      </Row>

      <Card title="Withholding lines" style={{ marginTop: 16 }} loading={loading}>
        <Table
          size="small"
          rowKey="settlement_id"
          columns={columns}
          dataSource={report?.rows || []}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
};

export default TdsFiling;
